import React, { useState, useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { hasMinRole } from "../lib/rbac";
import type { SystemRole } from "../lib/rbac";
import { ArrowUp } from "lucide-react";
import { useCreateOrchestratorSession } from "../hooks/use-orchestrator";
import { cn } from "@/lib/utils";
import { CONNECT_MCP_MIN_ROLE } from "@/lib/guides";
import { GettingStartedCard, useGettingStartedVisible } from "@/components/GettingStartedCard";
import { ConnectMcpModal } from "@/components/ConnectMcpModal";
import { FastnMark } from "@/components/sidebar-icons";
import { Icon } from "@/pages/prototype-icons";
import { AgentMark } from "@/components/AgentMark";
import { MCP_SERVER_URL, mcpClients } from "@/lib/mcp";
import { useOrgSuggestions, type OrgSuggestion } from "../hooks/use-onboarding";
import { homeTestIds } from "../testids/homeTestIds";
import { connectMcpTestIds } from "@/testids/connectMcpTestIds";
import "./home.css";

function getGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

interface SuggestionPill {
  label: string;
  path?: string;
  agentPrompt?: string;
}

function getSuggestions(role: SystemRole, personalized: OrgSuggestion[]): SuggestionPill[] {
  // canAgent matches ORCH_V2_ROLES in the agent service orchestrator routes.
  const canAgent = hasMinRole(role, "operator");

  // Personalized chips all launch the orchestrator, so they are only offered to
  // the roles that can actually use it. Below that, the navigation chips stand
  // as they always have — a personalized chip a viewer cannot run is a button
  // that 403s. All four or none: a mix of tailored and generic reads as broken.
  if (canAgent && personalized.length === 4) {
    return personalized.map((s) => ({ label: s.label, agentPrompt: s.prompt }));
  }

  const pills: SuggestionPill[] = [];
  if (canAgent) {
    pills.push({ label: "Recommend an integration", agentPrompt: "Recommend a connector for what we are building" });
    pills.push({ label: "Build an automation", agentPrompt: "Help me build an automation" });
  }
  // The prototype points these at the specific sub-pages, not the section root.
  // A chip that reads "Browse connectors" and lands on the Agent tab is a small
  // lie, and /integrations resolves to Agent now that it is the first child.
  pills.push({ label: "Browse connectors", path: "/integrations?tab=connectors" });
  if (hasMinRole(role, "viewer")) pills.push({ label: "View activity", path: "/activity/events" });
  return pills;
}

/**
 * Home — port of the approved build prototype's home surface. Markup and class
 * names follow the prototype; see home.css for the styling and why it carries
 * the prototype's own tokens.
 */
/* The marks shown under the Home button. Read from the registry rather than
   hardcoded so this strip cannot name an agent the dialog does not carry.
   No count: it said "and 11 more" from a literal and went stale the moment the
   Claude entries were reshuffled, and the exact number is not what anyone is
   reading this line for. */
const AGENT_CLIENTS = mcpClients(MCP_SERVER_URL);
const AGENT_PREVIEW_IDS = ["claude", "cursor", "codex"] as const;
const AGENT_PREVIEW = AGENT_PREVIEW_IDS.map(
  (id) => AGENT_CLIENTS.find((c) => c.id === id)!,
).filter(Boolean);

export function CommandCenter(): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userContext } = useAuth();
  const firstName = userContext?.displayName?.split(" ")[0] ?? user?.name?.split(" ")[0] ?? "there";
  const role = (userContext?.role ?? "end_user") as SystemRole;
  const canAgent = hasMinRole(role, "operator");

  // Suggestion chips are personalized per org. The hook returns [] until the
  // backend has a set to serve, and getSuggestions falls back to the static
  // chips for that whole window — so there is no skeleton and no layout shift,
  // the row just sharpens once.
  const { data: personalizedSuggestions = [] } = useOrgSuggestions();

  // Leaving the setup journey hands its use-case description over via router
  // state, so the person who just described their integration lands here with
  // the box prefilled instead of being asked to type it again.
  const [heroQuery, setHeroQuery] = useState(() => {
    const state = location.state as { setupUseCase?: string } | null;
    return state?.setupUseCase ?? "";
  });
  const [mcpOpen, setMcpOpen] = useState(false);
  // The hero centres itself with flex:1 and fills the viewport. With the
  // checklist below it that hid the card under the fold, so the hero gives up
  // the extra height whenever the card is actually going to render.
  const gettingStartedVisible = useGettingStartedVisible();
  const canConnectMcp = hasMinRole(role, CONNECT_MCP_MIN_ROLE);
  const askRef = useRef<HTMLTextAreaElement>(null);

  // Every query hands off to the Orchestrator: create a fresh session, then
  // deep-link to it carrying the query so the page auto-fires it as the first
  // message. The create hook prepends the session to the cache, so the
  // destination's unknown-threadId bounce-guard won't trip on navigation.
  const createOrchestratorSession = useCreateOrchestratorSession();
  const launchOrchestrator = useCallback(
    async (text: string) => {
      try {
        const session = await createOrchestratorSession.mutateAsync({});
        navigate(`/agent/${session.threadId}`, { state: { initialQuery: text } });
      } catch {
        // Session creation failed — stay on Home rather than dead-ending.
      }
    },
    [createOrchestratorSession, navigate],
  );

  const handlePill = (pill: SuggestionPill) => {
    if (pill.path) navigate(pill.path);
    else if (pill.agentPrompt) void launchOrchestrator(pill.agentPrompt);
  };

  const submit = () => {
    const q = heroQuery.trim();
    if (!q || createOrchestratorSession.isPending) return;
    setHeroQuery("");
    void launchOrchestrator(q);
  };

  /** The prototype's prompt is a textarea that grows with its content, so a
   *  long request stays readable instead of scrolling inside one line. */
  const autoGrow = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = "26px";
    el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
  };

  // Height is otherwise only set from onChange, so a prefilled description
  // would render clipped to one line until the first keystroke.
  useEffect(() => {
    if (askRef.current?.value) autoGrow(askRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={cn("home-root", "command-center")} data-testid={homeTestIds.commandCenter}>
      <div className={cn("page", "homePage", gettingStartedVisible && "homePage--withCard")}>
        <div className="homeMini">
          <div className="homeBrand">
            <span className="homeMark"><FastnMark /></span>
            <span className="homeWord">fastn</span>
          </div>
          <p className="homeGreet cc-greeting" data-testid={homeTestIds.greeting}>
            {getGreeting()}, {firstName}
          </p>

          {canAgent && (
            <div className={cn("askBox", "agent-chat")} data-testid={homeTestIds.agentHero}>
              <label className="fx-sr" htmlFor="homeAsk">What do you want to build?</label>
              <textarea
                id="homeAsk"
                ref={askRef}
                rows={1}
                className="agent-chat-input"
                placeholder="What do you want to build?"
                value={heroQuery}
                onChange={(e) => { setHeroQuery(e.target.value); autoGrow(e.target); }}
                onKeyDown={(e) => {
                  // Enter sends; Shift+Enter is a newline, which is the only
                  // reason a textarea is worth having here.
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    submit();
                  }
                }}
                data-testid={homeTestIds.agentHeroInput}
              />
              <button
                type="button"
                className="askGo"
                onClick={submit}
                disabled={!heroQuery.trim() || createOrchestratorSession.isPending}
                aria-label="Send"
                data-testid={homeTestIds.agentHeroSubmitButton}
              >
                <ArrowUp />
              </button>
            </div>
          )}

          <div className="askChips" data-testid={homeTestIds.suggestions}>
            {getSuggestions(role, personalizedSuggestions).map((pill, i) => (
              <button
                key={pill.label}
                type="button"
                className="askChip"
                data-testid={homeTestIds.suggestionPill(i)}
                onClick={() => handlePill(pill)}
              >
                {pill.label}
              </button>
            ))}
          </div>

          {/* Hands the fastn MCP server URL to whichever AI tool the user
              works in — see ConnectMcpModal. Gated to match the guide behind
              it, so no role is offered a button that dead-ends on "not
              available for your role". */}
          {canConnectMcp && (
            <button
              type="button"
              className="claudeBtn"
              data-testid={connectMcpTestIds.homeButton}
              onClick={() => setMcpOpen(true)}
            >
              <Icon name="plug" />
              Connect to your favourite agent
            </button>
          )}
          {/* Answers "which agents?" without a click, and gives the button
              something to catch the eye with that is not a colour the design
              system reserves for links and focus. */}
          {canConnectMcp && (
            <div className="claudeHint" data-testid={homeTestIds.connectMcpHint}>
              {AGENT_PREVIEW.map((client) => (
                <AgentMark key={client.id} mark={client.mark} />
              ))}
              <span>{AGENT_PREVIEW.map((c) => c.name).join(", ")} and more</span>
            </div>
          )}
        </div>
      </div>

      {/* Not in the prototype, which has no notion of a half-set-up account.
          Kept because it is a working feature and it self-hides once setup is
          done, so a settled account still sees the prototype's layout exactly. */}
      <div className="mx-auto w-full max-w-[620px] px-4 pb-8">
        <GettingStartedCard />
      </div>

      {mcpOpen && <ConnectMcpModal open source="home" onClose={() => setMcpOpen(false)} />}
    </div>
  );
}
